<template>
  <DialogFormWrapper>
    <template #form>
      <v-form
        id="swapInvoiceOrderForm"
        ref="swapInvoiceOrderForm"
        class="w-100"
        @submit.prevent="submitSwapInvoiceOrder"
      >
        <v-row style="row-gap: 16px">
          <v-col cols="12" class="pt-0">
            <span class="input-label d-block">Aktualnie podpięte zlecenie:</span>
            <template v-if="currentOrders.length">
              <div v-for="order in currentOrders" :key="order.id" class="chosen-client-details mb-1">
                {{ currentOrderLabel(order) }}
              </div>
            </template>
            <span v-else class="chosen-client-details">
              Brak podpiętego zlecenia
            </span>
          </v-col>

          <v-col cols="12">
            <v-autocomplete
              v-model="assignedOrderId"
              outlined
              :items="availableOrders"
              label="Nowe zlecenie"
              :item-text="orderItemText"
              item-value="id"
              :rules="[rules.required]"
              :loading="isFetchingOrders || isSearchingOrders"
              :disabled="isFetchingOrders || !currentOrders.length"
              :search-input.sync="searchQuery"
              no-filter
              placeholder="Szukaj zlecenia"
              append-icon="mdi-chevron-down"
              no-data-text="Brak zleceń do przepięcia"
              dense
              @change="setSelectedOrder"
            >
              <template #prepend-inner>
                <Icon name="search" size="small" class="mr-2 mt-1" />
              </template>
              <template v-if="hasMoreOrders" #append-item>
                <div v-intersect="onListEndIntersect" class="d-flex justify-center py-2">
                  <v-progress-circular v-if="isFetchingNextPage" indeterminate size="20" width="2" color="primary" />
                </div>
              </template>
            </v-autocomplete>
          </v-col>
        </v-row>
      </v-form>
    </template>
    <template #submit>
      <v-btn
        color="primary"
        class="base-hover"
        :loading="isProcessing"
        :disabled="isSubmitDisabled"
        type="submit"
        form="swapInvoiceOrderForm"
      >
        Przepnij zlecenie
      </v-btn>
    </template>
  </DialogFormWrapper>
</template>

<script>
import DialogFormWrapper from './Partials/DialogFormWrapper'
import afterFormSubmitted from '../../mixins/afterFormSubmitted'
import rules from '../../utils/validators'
import { mapState, mapActions } from 'vuex'
import debounce from 'lodash/debounce'

const ORDERS_PER_PAGE = 50

export default {
  components: {
    DialogFormWrapper
  },
  mixins: [afterFormSubmitted],
  data() {
    return {
      rules,
      assignedOrderId: null,
      unassignedOrderId: null,
      isFetchingOrders: false,
      isFetchingNextPage: false,
      isSearchingOrders: false,
      searchQuery: null,
      ordersQuery: '',
      selectedOrder: null
    }
  },
  computed: {
    ...mapState({
      isProcessing: state => state.invoice.isProcessing,
      invoice: state => state.invoice.entity,
      department: state => state.core.department,
      ordersList: state => state.orders.simplifiedList,
      ordersPagination: state => state.orders.simplifiedPagination
    }),
    hasMoreOrders() {
      const { page, pageCount } = this.ordersPagination
      return !this.isSearchingOrders && !!page && page < pageCount
    },
    currentOrders() {
      return this.invoice.orders || []
    },
    currentOrderIds() {
      return this.currentOrders.map(order => order.id)
    },
    availableOrders() {
      const orders = this.ordersList.filter(order => !this.currentOrderIds.includes(order.id))
      // keep selected order in items, otherwise autocomplete loses its label when search results don't include it
      const isSelectedOrderMissing = this.selectedOrder && !orders.some(order => order.id === this.selectedOrder.id)
      return isSelectedOrderMissing ? [this.selectedOrder, ...orders] : orders
    },
    isSubmitDisabled() {
      return (
        this.isProcessing ||
        this.isFetchingOrders ||
        !this.unassignedOrderId ||
        !this.assignedOrderId ||
        this.unassignedOrderId === this.assignedOrderId
      )
    }
  },
  watch: {
    searchQuery(value) {
      // autocomplete sets search input to selected item's text - it's not a search
      if (this.selectedOrder && value === this.orderItemText(this.selectedOrder)) return
      this.debouncedSearchOrders(value?.trim() || '')
    }
  },
  created() {
    this.debouncedSearchOrders = debounce(this.searchOrders, 700)
  },
  mounted() {
    // swap_order currently supports invoices with a single attached order.
    if (this.currentOrders.length === 1) {
      this.unassignedOrderId = this.currentOrders[0].id
    }

    this.isFetchingOrders = true
    this.fetchOrdersPage(1).finally(() => {
      this.isFetchingOrders = false
    })
  },
  beforeDestroy() {
    this.debouncedSearchOrders.cancel()
    this.clearSimplifiedOrdersList()
  },
  methods: {
    ...mapActions({
      getSimplifiedOrders: 'orders/getSimplifiedItems',
      clearSimplifiedOrdersList: 'orders/clearSimplifiedList',
      swapInvoiceOrder: 'invoice/swapInvoiceOrder'
    }),
    fetchOrdersPage(page) {
      return this.getSimplifiedOrders({
        params: {
          departmentId: this.department.id,
          page,
          itemsPerPage: ORDERS_PER_PAGE,
          filters: { query: this.ordersQuery || undefined }
        },
        append: page > 1
      })
    },
    searchOrders(query) {
      if (query === this.ordersQuery) return

      this.ordersQuery = query
      this.isSearchingOrders = true
      this.fetchOrdersPage(1).finally(() => {
        this.isSearchingOrders = false
      })
    },
    setSelectedOrder(id) {
      this.selectedOrder = this.availableOrders.find(order => order.id === id) || null
    },
    onListEndIntersect(entries, observer, isIntersecting) {
      if (!isIntersecting || this.isFetchingNextPage || !this.hasMoreOrders) return

      this.isFetchingNextPage = true
      this.fetchOrdersPage(this.ordersPagination.page + 1).finally(() => {
        this.isFetchingNextPage = false
      })
    },
    currentOrderLabel(order) {
      const parts = [
        order.refNumber,
        this.currentOrderClientName(order),
        order.formattedAddress,
        order.orderContainerTypeName
      ].filter(Boolean)
      return parts.join(' · ') || `#${order.id}`
    },
    currentOrderClientName(order) {
      if (order.clientName || order.client?.name) return order.clientName || order.client.name

      const simplified = this.ordersList.find(item => item.id === order.id)
      if (!simplified) return null
      if (simplified.clientName) return simplified.clientName

      const parsedParts = simplified.parsedData?.split(';').map(part => part.trim()) || []
      return parsedParts[1] || null
    },
    orderItemText(order) {
      return order.parsedData || order.refNumber || `#${order.id}`
    },
    submitSwapInvoiceOrder() {
      if (!this.$refs.swapInvoiceOrderForm.validate()) return

      this.swapInvoiceOrder({
        id: this.invoice.id,
        params: {
          unassignedOrderId: this.unassignedOrderId,
          assignedOrderId: this.assignedOrderId
        }
      }).then(resp => {
        this.afterFormSubmitted(resp.data?.success || 'Zlecenie przepięte.')
      })
    }
  }
}
</script>
